/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { FullProduct } from '@msdyn365-commerce/commerce-entities';
import { ICoreContext } from '@msdyn365-commerce/core';
import { Customer, ProductList, ProductListLine, ProductListsDataActions } from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions, FullProductInput, getFullProducts, getProductDetailsCriteriaFromActionInput, OrderTemplate
} from '@msdyn365-commerce-modules/retail-actions';
import { Button } from '@msdyn365-commerce-modules/utilities';
import React, { useState } from 'react';

import { AddToOrderTemplateDialog, IAddToOrderTemplateDialogResources } from '../add-to-order-template-dialog/add-to-order-template-dialog.component';
import {
    ILinesAddedToOrderTemplateDialogResources, LinesAddedToOrderTemplateDialog
} from '../lines-added-to-order-template-dialog/lines-added-to-order-template-dialog.component';
import { IOrderTemplateNameDialogResources } from '../order-template-name-dialog/order-template-name-dialog.component';

/**
 * Data for the add products to order template component.
 */
export interface IAddProductsToOrderTemplateData {
    orderTemplates: OrderTemplate[];
    products: ProductListLine[];

    customerInformation?: Customer;
}

/**
 * Resources for the add products to order template component.
 */
export interface IAddProductsToOrderTemplateResources
    extends IAddToOrderTemplateDialogResources, IOrderTemplateNameDialogResources, ILinesAddedToOrderTemplateDialogResources {

    addProductsToOrderTemplateButton: string;
    addProductsToOrderTemplateButtonTooltip: string;
}

/**
 * Props for the add products to order template component.
 */
export interface IAddProductsToOrderTemplateProps {
    className: string;

    context: ICoreContext;

    data: IAddProductsToOrderTemplateData;

    resources: IAddProductsToOrderTemplateResources;

    isDisabled?: boolean;
}

const modalTimeoutTime = 500;

/**
 * Add products to order template component.
 * @param props - Component props.
 * @returns React node.
 */
export const AddProductsToOrderTemplateComponent: React.FC<IAddProductsToOrderTemplateProps> = (props: IAddProductsToOrderTemplateProps) => {
    const [chosenOrderTemplate, setChosenOrderTemplate] = useState<ProductList | undefined>(undefined);
    const [addedProducts, setAddedProducts] = useState<FullProduct[] | undefined>(undefined);
    const [isAddToOrderTemplateModalOpen, setIsAddToOrderTemplateModalOpen] = useState(false);
    const [isLinesAddedToOrderTemplateDialogOpen, setIsLinesAddedToOrderTemplateDialogOpen] = useState(false);
    const [addedLinesCount, setAddedLinesCount] = useState(0);
    const [addedItemsCount, setAddedItemsCount] = useState(0);

    /**
     * Adds product to the selected order template.
     * @param orderTemplate - Order template to use for adding products.
     */
    const onOrderTemplateSelected = React.useCallback(async (orderTemplate: ProductList) => {
        setChosenOrderTemplate(orderTemplate);

        const linesToAdd: ProductListLine[] = props.data.products.map(line => {
            return {
                ...line,
                ProductListId: orderTemplate.Id
            };
        });

        const allProductInputs: FullProductInput[] = [];
        for (const listLine of linesToAdd) {
            if (listLine.ProductId) {
                allProductInputs.push(new FullProductInput(
                    listLine.ProductId,
                    props.context.actionContext.requestContext.apiSettings,
                    getProductDetailsCriteriaFromActionInput(props.context.actionContext)));
            }
        }

        const allProducts = await getFullProducts(allProductInputs, props.context.actionContext);

        const addedLines = await ProductListsDataActions.addProductListLinesAsync(
            { callerContext: props.context.actionContext }, orderTemplate.Id, linesToAdd);

        const quantities = addedLines.map(line => line.Quantity ?? 0);
        setAddedLinesCount(addedLines.length);
        setAddedItemsCount(ArrayExtensions.hasElements(quantities) ? ArrayExtensions.calculateSum(quantities) : 0);
        setAddedProducts(allProducts);

        setIsAddToOrderTemplateModalOpen(false);
        setTimeout(() => {
            setIsLinesAddedToOrderTemplateDialogOpen(true);
        }, modalTimeoutTime);
    }, [props.context.actionContext, props.data.products]);

    const onAddProductsToOrderTemplateClick = React.useCallback(() => {
        setIsAddToOrderTemplateModalOpen(true);
    }, []);

    if (!props.data.customerInformation?.IsB2b) {
        return null;
    }

    const addToOrderTemplateReference: React.RefObject<HTMLButtonElement> = React.createRef();

    return (
        <div className={props.className}>
            <AddToOrderTemplateDialog
                className='msc-add-to-order-template-dialog'
                id=''
                typeName=''
                modalOpen={isAddToOrderTemplateModalOpen}
                setModalOpen={setIsAddToOrderTemplateModalOpen}
                context={props.context}
                data={{ orderTemplates: props.data.orderTemplates }}
                addToOrderTemplateDialogResources={props.resources}
                createOrderTemplateDialogResources={props.resources}
                onOrderTemplateSelected={onOrderTemplateSelected}
            />
            {
                chosenOrderTemplate && addedProducts && <LinesAddedToOrderTemplateDialog
                    className='msc-lines-added-to-order-template-dialog'
                    id=''
                    typeName=''
                    context={props.context}
                    dialogStrings={props.resources}
                    data={{
                        linesCount: addedLinesCount,
                        itemsCount: addedItemsCount,
                        orderTemplate: chosenOrderTemplate,
                        products: addedProducts
                    }}
                    modalOpen={isLinesAddedToOrderTemplateDialogOpen}
                />
            }
            <Button
                className={`${props.className}__add-button`}
                onClick={onAddProductsToOrderTemplateClick}
                title={props.resources.addProductsToOrderTemplateButton}
                innerRef={addToOrderTemplateReference}
                disabled={props.isDisabled}
            >
                {props.resources.addProductsToOrderTemplateButton}
            </Button>
        </div>
    );
};

